import axios from 'axios';

// API URL 가져오기
const API_URL = import.meta.env.API_URL || 'http://localhost:3000';

// JWT 토큰 관련 상수
const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_EMAIL_KEY = 'userEmail';
const ACCESS_TOKEN_KEY = 'accessToken';
const API_KEY_KEY = 'apiKey';

/**
 * axios 인스턴스 생성 및 설정
 */
export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * API 키를 초기화하는 함수
 * @returns {Promise<string>} - 초기화된 API 키
 */
export const initApiKey = async () => {
  try {
    // 기기 및 브라우저 정보를 수집하여 fingerprint 생성
    const fingerprint = generateFingerprint();
    
    // 서버에 API 키 요청
    const response = await axios.post(`${API_URL}/user/auth/init-key`, { fingerprint });
    console.log('initApiKey - 응답 구조 확인:', JSON.stringify(response.data));
    console.log({
      response,
      compare1: Boolean(response.data && response.data.xApiKey),
      compare2: Boolean(response.data && response.data.data && response.data.data.xApiKey)
    })
    
    // API 키 받아서 로컬 스토리지에 저장
    if (response.data && response.data.xApiKey) {
      const apiKey = response.data.xApiKey;
      localStorage.setItem(API_KEY_KEY, apiKey);
      console.log('API 키 초기화 성공');
      return apiKey;
    } else if (response.data && response.data.data && response.data.data.xApiKey) {
      // 응답이 { apiKey: "..." } 형태인 경우
      const apiKey = response.data.data.xApiKey;
      localStorage.setItem(API_KEY_KEY, apiKey);
      console.log('API 키 초기화 성공 (직접 응답)');
      return apiKey;
    } else {
      console.error('API 키 응답 형식이 잘못되었습니다:', response.data);
      throw new Error('API 키 초기화 실패: 응답 형식 오류');
    }
  } catch (error) {
    console.error('API 키 초기화 실패:', error);
    throw error;
  }
};

/**
 * 저장된 API 키 가져오기
 * @returns {string|null} - 저장된 API 키 또는 null
 */
export const getApiKey = () => {
  return localStorage.getItem(API_KEY_KEY);
};

/**
 * 인증 요청 인터셉터 설정
 * 요청 시 헤더에 토큰 추가
 */
apiClient.interceptors.request.use(
  async (config) => {
    // API 키 헤더 추가
    const apiKey = localStorage.getItem(API_KEY_KEY);
    if (apiKey) {
      config.headers['x-api-key'] = apiKey;
    }
    
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      // 토큰 만료 시간 확인
      const tokenExpiry = getTokenExpiryTime(token);
      if (tokenExpiry) {
        const now = new Date();
        const expiryTime = new Date(tokenExpiry * 1000);
        const timeLeft = Math.floor((expiryTime - now) / 1000);
        
        // 토큰이 만료되었거나 5분(300초) 이내에 만료될 예정인 경우 갱신 시도
        if (timeLeft <= 300) {
          console.log(`토큰이 곧 만료됩니다(${timeLeft}초 남음). 갱신을 시도합니다.`);
          try {
            const refreshed = await preemptiveTokenRefresh();
            if (refreshed) {
              // 갱신된 토큰으로 업데이트
              const newToken = localStorage.getItem(TOKEN_KEY);
              config.headers.Authorization = `Bearer ${newToken}`;
              console.log('갱신된 토큰으로 요청 헤더가 업데이트되었습니다.');
            }
          } catch (error) {
            console.error('사전 토큰 갱신 실패:', error);
            // 갱신 실패 시에도 현재 토큰으로 요청 계속 진행
          }
        } else {
          // 개발 모드에서만 출력되는 로그
          if (import.meta.env.DEV) {
            console.log(`API 요청: ${config.method.toUpperCase()} ${config.url}`);
            console.log(`토큰 만료까지 남은 시간: ${Math.floor(timeLeft / 60)}분 ${timeLeft % 60}초`);
          }
        }
      }
      
      // 현재 토큰 설정
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * 응답 인터셉터 설정
 * 토큰 만료 시 자동으로 토큰 갱신
 */
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // 401 에러이고 토큰 만료된 경우 토큰 갱신
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        console.log('리프레시 토큰 확인:', refreshToken);
        console.log('refresh token stringify:', JSON.stringify({ refreshToken }));
        if (!refreshToken) {
          // 리프레시 토큰이 없으면 로그아웃 (콜백 없이 호출)
          logout();
          return Promise.reject(error);
        }

        console.log('토큰 만료됨. 리프레시 토큰으로 갱신 시도...');
        console.log('원래 요청 URL:', originalRequest.url);
        console.log('원래 요청 메소드:', originalRequest.method);
        
        // 토큰 갱신 요청
        const response = await axios.post(`${API_URL}/user/auth/refresh-token`, {
          refreshToken: refreshToken,
        });
        console.log('토큰 갱신 응답--------- authService:', response.data);
        console.log('refreshTokenIfNeeded - 응답 구조 확인:', JSON.stringify(response.data));
        console.log('refreshTokenIfNeeded - 응답 내 데이터 경로:', response.data?.data ? '있음' : '없음');
        console.log('응답 내 토큰 확인:', response.data.token);
        console.log('응답 내 리프레시 토큰 확인:', response.data.refreshToken);
        
        // 응답 구조 세부 분석
        if (response.data) {
          console.log('응답에 data 있음:', !!response.data);
          console.log('응답 키:', Object.keys(response.data));
          
          if (response.data.data) {
            console.log('응답에 data.data 있음:', !!response.data.data);
            console.log('data.data 키:', Object.keys(response.data.data));
            
            if (response.data.data.token) {
              console.log('응답에 data.data.token 있음:', !!response.data.data.token);
            }
            
            if (response.data.data.refreshToken) {
              console.log('응답에 data.data.refreshToken 있음:', !!response.data.data.refreshToken);
            }
          }
        }
        
        // 새 토큰 저장
        if (response.data) {
          let newToken, newRefreshToken;
          
          // 응답 구조가 { data: { token, refreshToken } } 형태인 경우
          if (response.data.data && response.data.data.token) {
            newToken = response.data.data.token;
            newRefreshToken = response.data.data.refreshToken;
            console.log('refreshTokenIfNeeded - 응답 구조 { data: { token, refreshToken } } 사용');
          } 
          // 응답 구조가 { token, refreshToken } 형태인 경우
          else if (response.data.token) {
            newToken = response.data.token;
            newRefreshToken = response.data.refreshToken;
            console.log('refreshTokenIfNeeded - 응답 구조 { token, refreshToken } 사용');
          }
          
          if (newToken && newRefreshToken) {
            localStorage.setItem(TOKEN_KEY, newToken);
            localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
            
            console.log('토큰 갱신 성공!');
            // 새 토큰의 만료 시간 계산 및 로그 출력
            logTokenExpirationTime(newToken);
            
            console.log('토큰 갱신 성공! 원래 요청 재시도...');
            
            // 원래 요청 재시도
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            console.log('재시도 요청 헤더(Authorization):', `Bearer ${newToken.substring(0, 15)}...`);
            
            try {
              const retryResponse = await apiClient(originalRequest);
              console.log('원래 요청 재시도 성공!', originalRequest.url);
              console.log('응답 상태:', retryResponse.status);
              console.log('응답 데이터 구조:', Object.keys(retryResponse.data));
              return retryResponse;
            } catch (retryError) {
              console.error('원래 요청 재시도 실패:', retryError);
              // 재시도 실패 시에도 강제 로그아웃
              logout();
              return Promise.reject(retryError);
            }
          } else {
            // 토큰 갱신은 성공했지만 새 토큰이 없는 경우
            console.error('토큰 갱신 응답이 올바르지 않습니다');
            logout();
            return Promise.reject(new Error('토큰 갱신 실패: 유효한 토큰 없음'));
          }
        } else {
          // 토큰 갱신은 성공했지만 새 토큰이 없는 경우
          console.error('토큰 갱신 응답이 올바르지 않습니다');
          logout();
          return Promise.reject(new Error('토큰 갱신 실패: 유효한 토큰 없음'));
        }
      } catch (refreshError) {
        console.error('토큰 갱신 실패:', refreshError);
        // 토큰 갱신 실패 시 로그아웃
        logout();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * JWT 토큰에서 만료 시간을 추출하고 로컬 시간으로 변환하여 로그에 출력
 * @param {string} token - JWT 토큰
 */
const logTokenExpirationTime = (token) => {
  try {
    // 토큰 파싱
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    const { exp } = JSON.parse(jsonPayload);
    
    // 현재 시간과 만료 시간의 차이 계산
    const expirationTime = new Date(exp * 1000);
    const currentTime = new Date();
    const timeDiff = expirationTime - currentTime;
    
    // 만료된 경우 (timeDiff가 음수인 경우) 토큰 갱신 시도
    if (timeDiff <= 0) {
      console.log('토큰이 이미 만료되었습니다. 갱신을 시도합니다.');
      // 비동기적으로 토큰 갱신 로직 호출
      refreshTokenIfNeeded(token);
      return;
    }
    
    // 남은 시간 계산
    const hours = Math.floor(timeDiff / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
    
    console.log(`새 토큰 발급 완료!`);
    console.log(`토큰 만료 시간: ${expirationTime.toLocaleString()}`);
    console.log(`남은 시간: ${hours}시간 ${minutes}분 ${seconds}초`);
  } catch (error) {
    console.error('토큰 만료 시간 계산 실패:', error);
  }
};

/**
 * 토큰이 만료되었거나 만료가 임박한 경우 갱신을 시도하는 함수
 * @param {string} token - 현재 JWT 토큰
 */
const refreshTokenIfNeeded = async (token) => {
  try {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    console.log('refreshTokenIfNeeded - 리프레시 토큰 확인:', refreshToken);
    console.log('refreshTokenIfNeeded - refresh token stringify:', JSON.stringify({ refreshToken }));
    if (!refreshToken) {
      console.error('리프레시 토큰이 없어 갱신을 시도할 수 없습니다.');
      logout();
      return;
    }

    console.log('만료된 토큰 감지. 리프레시 토큰으로 갱신 시도...');
    
    // 토큰 갱신 요청
    const response = await axios.post(`${API_URL}/user/auth/refresh-token`, {
      refreshToken: refreshToken,
    });
    
    console.log('refreshTokenIfNeeded - 응답 구조 확인:', JSON.stringify(response.data));
    console.log('refreshTokenIfNeeded - 응답 내 데이터 경로:', response.data?.data ? '있음' : '없음');
    
    // 새 토큰 저장
    if (response.data) {
      let newToken, newRefreshToken;
      
      // 응답 구조가 { data: { token, refreshToken } } 형태인 경우
      if (response.data.data && response.data.data.token) {
        newToken = response.data.data.token;
        newRefreshToken = response.data.data.refreshToken;
        console.log('refreshTokenIfNeeded - 응답 구조 { data: { token, refreshToken } } 사용');
      } 
      // 응답 구조가 { token, refreshToken } 형태인 경우
      else if (response.data.token) {
        newToken = response.data.token;
        newRefreshToken = response.data.refreshToken;
        console.log('refreshTokenIfNeeded - 응답 구조 { token, refreshToken } 사용');
      }
      
      if (newToken && newRefreshToken) {
        localStorage.setItem(TOKEN_KEY, newToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
        
        console.log('토큰 갱신 성공!');
        // 새 토큰의 만료 시간 계산 및 로그 출력
        logTokenExpirationTime(newToken);
      } else {
        console.error('토큰 갱신 응답이 올바르지 않습니다');
        logout();
      }
    } else {
      console.error('토큰 갱신 응답이 올바르지 않습니다');
      logout();
    }
  } catch (error) {
    console.error('토큰 갱신 실패:', error);
    // 토큰 갱신 실패 시 로그아웃
    logout();
  }
};

/**
 * 토큰에서 만료 시간을 추출하는 함수
 * @param {string} token - JWT 토큰
 * @returns {number|null} - 만료 시간(초) 또는 null
 */
const getTokenExpiryTime = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    const { exp } = JSON.parse(jsonPayload);
    return exp;
  } catch (error) {
    console.error('토큰 만료 시간 추출 실패:', error);
    return null;
  }
};

/**
 * 토큰이 만료되었는지 확인하는 함수
 * @param {string} token - JWT 토큰
 * @returns {boolean|null} - 만료 여부 또는 null (토큰 파싱 실패 시)
 */
export const isTokenExpired = (token) => {
  if (!token) return true;
  
  const exp = getTokenExpiryTime(token);
  if (!exp) return true; // 파싱 실패 시 안전하게 만료된 것으로 처리
  
  const currentTime = Math.floor(Date.now() / 1000); // 현재 시간(초)
  return exp <= currentTime; // 만료 시간이 현재 시간보다 작거나 같으면 만료된 것
};

/**
 * 토큰 만료까지 남은 시간(초)을 계산하는 함수
 * @param {string} token - JWT 토큰
 * @returns {number|null} - 만료까지 남은 시간(초) 또는 null (토큰 파싱 실패 시)
 */
export const getTimeUntilExpiration = (token) => {
  if (!token) return null;
  
  const exp = getTokenExpiryTime(token);
  if (!exp) return null;
  
  const currentTime = Math.floor(Date.now() / 1000);
  return exp - currentTime; // 음수면 이미 만료된 것
};

/**
 * 로그인 함수
 * @param {string} email - 사용자 이메일
 * @param {string} password - 사용자 비밀번호
 * @returns {Promise} - 로그인 결과
 */
export const login = async (email, password) => {
  try {
    // 로그인 요청 전에 토큰이 남아있는지 확인하고 있다면 로컬 스토리지 정리
    const existingToken = localStorage.getItem(TOKEN_KEY);
    if (existingToken) {
      console.log('기존 토큰이 발견되어 로컬 스토리지를 정리합니다.');
      clearLocalStorage();
    }

    const response = await axios.post(`${API_URL}/user/auth/login`, {
      email,
      password,
    });

    console.log('로그인 응답 --------- authService:', response.data);
    console.log('로그인 응답 구조 확인:', JSON.stringify(response.data));
    
    // 로그인 성공 시 토큰 저장
    if (response.data) {
      let token, refreshToken;
      
      // 응답 구조가 { data: { token, refreshToken } } 형태인 경우
      if (response.data.data && response.data.data.token) {
        token = response.data.data.token;
        refreshToken = response.data.data.refreshToken;
        console.log('로그인 - 응답 구조 { data: { token, refreshToken } } 사용');
      } 
      // 응답 구조가 { token, refreshToken } 형태인 경우
      else if (response.data.token) {
        token = response.data.token;
        refreshToken = response.data.refreshToken;
        console.log('로그인 - 응답 구조 { token, refreshToken } 사용');
      }
      
      if (token && refreshToken) {
        localStorage.setItem(TOKEN_KEY, token);
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
        localStorage.setItem(USER_EMAIL_KEY, email);
        
        // 토큰 만료 시간 계산 및 로그 출력
        logTokenExpirationTime(token);
        
        // API 키 초기화
        try {
          await initApiKey();
        } catch (apiKeyError) {
          console.error('로그인 성공 후 API 키 초기화 실패:', apiKeyError);
          // API 키 초기화 실패해도 로그인 프로세스는 계속 진행
        }
      } else {
        console.warn('로그인 응답에 토큰 정보가 없습니다:', response.data);
      }
      
      return response.data;
    }
    
    return response.data;
  } catch (error) {
    console.error('로그인 실패:', error);
    
    // 에러 객체를 보강하여 더 많은 정보 제공
    if (error.response) {
      // 서버에서 응답이 왔지만 에러 상태 코드인 경우
      error.userMessage = 'Login failed.';
      const strInputCheckNeeded = 'Please check your ID or password.';
      
      if (error.response.status === 401) {
        error.userMessage = strInputCheckNeeded;
      } else if (error.response.status === 404) {
        error.userMessage = strInputCheckNeeded;
      } else if (error.response.status >= 500) {
        error.userMessage = 'A server error occurred. Please try again later.';
      }
    } else if (error.request) {
      // 요청이 전송되었지만 응답이 없는 경우
      error.userMessage = 'Cannot connect to the server. Please check your internet connection.';
    } else {
      // 요청 설정 중 에러 발생
      error.userMessage = 'An error occurred during the login request.';
    }
    
    throw error;
  }
};

/**
 * 로그아웃 함수
 * @param {Function} onLogout - 로그아웃 완료 후 실행할 콜백 함수
 */
export const logout = (onLogout) => {
  // 로그아웃 API 호출 (로컬 스토리지에서 토큰을 삭제하기 전에 호출)
  const token = localStorage.getItem(TOKEN_KEY);
  const userEmail = localStorage.getItem(USER_EMAIL_KEY);
  
  // 토큰이 있든 없든 항상 로컬 스토리지를 정리하도록 함
  if (token) {
    // 로그아웃 API 호출 시도 (성공 여부와 관계없이 로그아웃 진행)
    apiClient.post('/user/auth/logout', { email: userEmail })
      .then(() => {
        console.log('로그아웃 API 호출 성공');
      })
      .catch(error => {
        // 40x 에러가 와도 무시하고 계속 진행
        console.log('로그아웃 API 호출 결과와 무관하게 로그아웃 진행');
      })
      .finally(() => {
        // API 호출 결과와 관계없이 항상 로컬 스토리지 정리
        clearLocalStorage();
        
        // 페이지 리프레시 제거
        console.log('로그아웃 처리 완료 (리프레시 없음)');
        
        // 콜백 함수가 있으면 실행
        if (typeof onLogout === 'function') {
          onLogout();
        }
      });
  } else {
    // 토큰이 없는 경우 바로 로컬 스토리지 정리
    clearLocalStorage();
    
    // 페이지 리프레시 제거
    console.log('로그아웃 처리 완료 (리프레시 없음)');
    
    // 콜백 함수가 있으면 실행
    if (typeof onLogout === 'function') {
      onLogout();
    }
  }
};

/**
 * 로컬 스토리지 정리 함수
 */
const clearLocalStorage = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_EMAIL_KEY);
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(API_KEY_KEY);
};

/**
 * 현재 로그인 상태 확인
 * @returns {boolean} - 로그인 여부
 */
export const isAuthenticated = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return !!token;
};

/**
 * 현재 사용자 이메일 가져오기
 * @returns {string|null} - 사용자 이메일 또는 null
 */
export const getCurrentUserEmail = () => {
  return localStorage.getItem(USER_EMAIL_KEY);
};

/**
 * 비밀번호 초기화 링크 요청 함수
 * @param {string} email - 사용자 이메일
 * @returns {Promise} - 비밀번호 초기화 링크 요청 결과
 */
export const requestPasswordReset = async (email) => {
  try {
    // 실제 API 호출
    const response = await axios.post(`${API_URL}/user/auth/pwd/reset`, {
      email
    });
    
    return response.data;
  } catch (error) {
    console.error('비밀번호 초기화 링크 요청 실패:', error);
    
    let errorMessage = '비밀번호 초기화 링크 요청에 실패했습니다.';
    
    if (error.response) {
      // 서버에서 반환된 에러 메시지가 있으면 사용
      if (error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      } else {
        // 상태 코드별 처리
        if (error.response.status === 400) {
          errorMessage = '이메일 형식이 올바르지 않습니다.';
        } else if (error.response.status === 404) {
          errorMessage = '등록되지 않은 이메일입니다.';
        } else if (error.response.status >= 500) {
          errorMessage = '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
        }
      }
    } else if (error.request) {
      errorMessage = '서버에 연결할 수 없습니다. 인터넷 연결을 확인해주세요.';
    }
    
    error.userMessage = errorMessage;
    throw error;
  }
};

/**
 * 비밀번호 재설정 함수
 * @param {string} token - 비밀번호 초기화 토큰 (URL에서 추출)
 * @param {string} newPassword - 새 비밀번호
 * @returns {Promise} - 비밀번호 재설정 결과
 */
export const resetPassword = async (token, newPassword) => {
  try {
    // 실제 API 호출 시 아래 주석 해제하고 사용
    // const response = await axios.post(`${API_URL}/user/auth/reset-password`, {
    //   token,
    //   newPassword
    // });
    // return response.data;

    // 개발용 가상 응답
    return {
      success: true,
      message: '비밀번호가 성공적으로 재설정되었습니다.'
    };
  } catch (error) {
    console.error('비밀번호 재설정 실패:', error);
    
    let errorMessage = '비밀번호 재설정에 실패했습니다.';
    
    if (error.response) {
      if (error.response.status === 400) {
        errorMessage = '유효하지 않은 토큰이거나 만료된 토큰입니다.';
      } else if (error.response.status >= 500) {
        errorMessage = '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
      }
    } else if (error.request) {
      errorMessage = '서버에 연결할 수 없습니다. 인터넷 연결을 확인해주세요.';
    }
    
    error.userMessage = errorMessage;
    throw error;
  }
};

/**
 * 토큰이 만료되기 전에 사전에 갱신하는 함수
 * @returns {Promise<boolean>} 갱신 성공 여부
 */
const preemptiveTokenRefresh = async () => {
  try {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    console.log('preemptiveTokenRefresh - 리프레시 토큰 확인:', refreshToken);
    console.log('preemptiveTokenRefresh - refresh token stringify:', JSON.stringify({ refreshToken }));
    if (!refreshToken) {
      console.error('리프레시 토큰이 없어 사전 갱신을 시도할 수 없습니다.');
      return false;
    }

    console.log('사전 토큰 갱신 시도...');
    
    // 토큰 갱신 요청
    const response = await axios.post(`${API_URL}/user/auth/refresh-token`, {
      refreshToken: refreshToken,
    });
    
    console.log('preemptiveTokenRefresh - 응답 구조 확인:', JSON.stringify(response.data));
    console.log('preemptiveTokenRefresh - 응답 내 데이터 경로:', response.data?.data ? '있음' : '없음');
    
    // 새 토큰 저장
    if (response.data) {
      let newToken, newRefreshToken;
      
      // 응답 구조가 { data: { token, refreshToken } } 형태인 경우
      if (response.data.data && response.data.data.token) {
        newToken = response.data.data.token;
        newRefreshToken = response.data.data.refreshToken;
        console.log('preemptiveTokenRefresh - 응답 구조 { data: { token, refreshToken } } 사용');
      } 
      // 응답 구조가 { token, refreshToken } 형태인 경우
      else if (response.data.token) {
        newToken = response.data.token;
        newRefreshToken = response.data.refreshToken;
        console.log('preemptiveTokenRefresh - 응답 구조 { token, refreshToken } 사용');
      }
      
      if (newToken && newRefreshToken) {
        localStorage.setItem(TOKEN_KEY, newToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
        
        console.log('사전 토큰 갱신 성공!');
        // 새 토큰의 만료 시간 로그 출력
        logTokenExpirationTime(newToken);
        return true;
      } else {
        console.error('토큰 갱신 응답이 올바르지 않습니다');
        return false;
      }
    } else {
      console.error('토큰 갱신 응답이 올바르지 않습니다');
      return false;
    }
  } catch (error) {
    console.error('사전 토큰 갱신 실패:', error);
    return false;
  }
};

/**
 * 브라우저 및 장치 정보를 기반으로 fingerprint 생성
 * @returns {Object} - 장치 식별에 필요한 정보들
 */
const generateFingerprint = () => {
  // 플랫폼 정보
  const platform = {
    userAgent: navigator.userAgent,
    platform: navigator.platform, 
    vendor: navigator.vendor,
    language: navigator.language,
    cpuCores: navigator.hardwareConcurrency || 'unknown',
    deviceMemory: navigator.deviceMemory || 'unknown',
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    colorDepth: window.screen.colorDepth,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffset: new Date().getTimezoneOffset(),
  };
  
  // 브라우저 정보
  const browserInfo = {
    cookiesEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack,
    plugins: Array.from(navigator.plugins || []).map(p => p.name).join(','),
    canvas: getCanvasFingerprint(),
    localStorage: isLocalStorageAvailable(),
    sessionStorage: isSessionStorageAvailable(),
  };
  
  // 네트워크 정보
  const networkInfo = {
    connection: navigator.connection ? {
      effectiveType: navigator.connection.effectiveType,
      rtt: navigator.connection.rtt,
      downlink: navigator.connection.downlink,
    } : 'unknown',
  };
  
  // 고유 ID 생성 (uuid v4 형식)
  const uuid = generateUUID();
  
  return {
    id: uuid,
    platform,
    browser: browserInfo,
    network: networkInfo,
    timestamp: new Date().toISOString(),
  };
};

/**
 * Canvas fingerprint 생성
 * @returns {string} - 캔버스 데이터 해시값
 */
const getCanvasFingerprint = () => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) return 'canvas-not-supported';
    
    // 캔버스에 그리기
    canvas.width = 200;
    canvas.height = 50;
    
    // 텍스트 그리기
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    
    // 다른 색상의 텍스트
    ctx.fillStyle = '#069';
    ctx.fillText('Fingerprint', 2, 15);
    
    // 그라데이션 추가
    const gradient = ctx.createLinearGradient(0, 0, 200, 0);
    gradient.addColorStop(0, 'red');
    gradient.addColorStop(0.5, 'green');
    gradient.addColorStop(1, 'blue');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 25, 200, 25);
    
    return canvas.toDataURL().substr(-50); // 마지막 50자만 반환
  } catch (e) {
    return 'canvas-error';
  }
};

/**
 * localStorage 가용성 확인
 * @returns {boolean}
 */
const isLocalStorageAvailable = () => {
  try {
    const testKey = '_test_' + Math.random();
    localStorage.setItem(testKey, testKey);
    const result = localStorage.getItem(testKey) === testKey;
    localStorage.removeItem(testKey);
    return result;
  } catch (e) {
    return false;
  }
};

/**
 * sessionStorage 가용성 확인
 * @returns {boolean}
 */
const isSessionStorageAvailable = () => {
  try {
    const testKey = '_test_' + Math.random();
    sessionStorage.setItem(testKey, testKey);
    const result = sessionStorage.getItem(testKey) === testKey;
    sessionStorage.removeItem(testKey);
    return result;
  } catch (e) {
    return false;
  }
};

/**
 * UUID v4 생성 함수
 * @returns {string} - 생성된 UUID
 */
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

export default {
  login,
  logout,
  isAuthenticated,
  getCurrentUserEmail,
  apiClient,
  isTokenExpired,
  getTimeUntilExpiration,
  initApiKey,
  getApiKey
}; 